import { Card, CardContent } from "@/components/ui/card";
import { CenteredSpinner } from "@/lib/components/CenteredSpinner";
import { EmptyStateWithError } from "@/lib/components/errors/EmptyStateWithError";
import { QueryErrorDisplay } from "@/lib/components/errors/QueryErrorDisplay";
import { useTeamMembers } from "../hooks";
import type { TeamMemberFilters } from "../types";
import { TeamMemberCard } from "./team-member-card";

interface TeamMemberListProps {
  statusFilter?: "active" | "inactive" | "all";
  filters?: TeamMemberFilters;
}

export function TeamMemberList({ statusFilter = "active", filters }: TeamMemberListProps) {
  const { activeTeamMembers, inactiveTeamMembers, teamMembers, loading, error, refetch } =
    useTeamMembers({
      filters: {
        ...filters,
        isActive: statusFilter === "all" ? undefined : statusFilter === "active",
      },
    });

  // Select team members based on status filter
  const members =
    statusFilter === "inactive"
      ? inactiveTeamMembers
      : statusFilter === "active"
        ? activeTeamMembers
        : teamMembers;

  // Loading state
  if (loading && members.length === 0) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-12">
          <CenteredSpinner size="default" />
        </CardContent>
      </Card>
    );
  }

  // Error state
  if (error && members.length === 0) {
    return (
      <Card>
        <CardContent className="py-8">
          <QueryErrorDisplay
            error={{
              type: "GRAPHQL_ERROR",
              message: error.message || "Failed to load team members",
              retryable: true,
            }}
            onRetry={refetch}
          />
        </CardContent>
      </Card>
    );
  }

  // Empty state
  if (!loading && members.length === 0) {
    const title =
      statusFilter === "inactive"
        ? "No inactive team members"
        : statusFilter === "active"
          ? "No active team members"
          : "No team members found";

    const description =
      statusFilter === "inactive"
        ? "Inactive team members will appear here."
        : statusFilter === "active"
          ? "Add your first team member to get started."
          : "Try adjusting your search or filters.";

    return (
      <Card>
        <CardContent className="py-12">
          <EmptyStateWithError title={title} description={description} error={null} />
        </CardContent>
      </Card>
    );
  }

  // Success state
  return (
    <div className="space-y-4">
      {members.map((member) => (
        <TeamMemberCard key={member.id} member={member} />
      ))}
    </div>
  );
}
